import { useState, useRef, useCallback, useEffect } from 'react';
import type { YoutubeSlideHandle } from '../components/YoutubeSlide';
import { identidadeSonoraPlayerRepository } from '../../data/identidadeSonoraPlayerRepository';
import { selecionarClipeLocucao } from '../../domain/usecases/selecionarClipeLocucao';
import { deveDispararLocucao } from '../../domain/usecases/deveDispararLocucao';
import { mediaCache } from '@/shared/lib/mediaCache';
import type { IdentidadeSonoraClipe } from '@/shared/types';

/** Duck relativo ao volume atual do YouTube. A narração entra POR CIMA da
 *  música, não no lugar dela: com 0.18 (valor original) a música caía para
 *  ~1/5 enquanto a narração tocava no nível cheio — ~5x de diferença de ganho,
 *  que é o que fazia a locução soar agressiva. Este número e o `ALVO_RMS` de
 *  `medirGanhoAudio.ts` são as duas pontas do mesmo equilíbrio: mexer em um
 *  pede conferir o outro. Fixo, não configurável nesta v1. */
const DUCK_RATIO = 0.4;

/** Narração sem medição de nível (enviada antes do nivelamento existir) toca
 *  no nível cru do arquivo — comportamento antigo. A página admin mede as
 *  pendentes ao abrir. */
const GANHO_PADRAO = 1;

/** Restaura o volume automaticamente se nem `ended` nem `error` dispararem
 *  (autoplay travado silenciosamente, stream congelado). Clipes são de 3-5s;
 *  a margem cobre variação de rede sem deixar a música abaixada por faixas inteiras. */
const SAFETY_TIMEOUT_MS = 12_000;

/** A TV roda 24/7 — sem isso, ligar/desligar ou trocar clipes no admin só
 *  chegaria na próxima reinicialização. Mesmo padrão do Active Sync Backup
 *  de `useJukeboxQueue.ts`. */
const RECARGA_CONFIG_MS = 3 * 60 * 1_000;

export interface UseIdentidadeSonoraParams {
  telaId:          string | undefined;
  /** modoPlaylistJukebox — mesma condição usada por useRaioXMusica. */
  habilitado:      boolean;
  /** Mesmo videoId observado por useRaioXMusica (pedido do cliente ou item do deck). */
  videoIdAtual:    string | null;
  ytSlideRef:      React.RefObject<YoutubeSlideHandle | null>;
  playerVolumeRef: React.RefObject<number>;
  /** TV mutada pelo Controle Remoto — a locução não dispara nesse estado. */
  playerMutedRef:  React.RefObject<boolean>;
  /** Compartilhado com usePlayerRemoteState: enquanto true, o setVolume do
   *  remoto não chama setVolume no YouTube da playlist. */
  locucaoAtivaRef: React.RefObject<boolean>;
}

export interface UseIdentidadeSonoraReturn {
  locucaoAudioRef: React.RefObject<HTMLAudioElement | null>;
  locucaoSrc:      string | null;
  handleLocucaoEnded(): void;
  handleLocucaoError(): void;
}

export function useIdentidadeSonora({
  telaId, habilitado, videoIdAtual, ytSlideRef, playerVolumeRef, playerMutedRef,
  locucaoAtivaRef,
}: UseIdentidadeSonoraParams): UseIdentidadeSonoraReturn {
  const [locucaoSrc, setLocucaoSrc] = useState<string | null>(null);
  const [locucaoTick, setLocucaoTick] = useState(0);
  const locucaoAudioRef = useRef<HTMLAudioElement>(null);

  const ativoRef            = useRef(false);
  const intervaloRef        = useRef(0);
  const clipesRef           = useRef<IdentidadeSonoraClipe[]>([]);
  const contadorFaixasRef   = useRef(0);
  const volumePreLocucaoRef = useRef(100);
  const ganhoAtualRef       = useRef(GANHO_PADRAO);
  const videoIdAnteriorRef  = useRef<string | null>(null);
  const safetyTimeoutRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** URL remota → Blob URL local já baixado. Sem isto o download do arquivo
   *  começava no instante do disparo: a música abaixava na hora e a fala só
   *  entrava depois, dando a impressão de narração cortada no começo. */
  const blobsRef            = useRef(new Map<string, string>());
  /** Lido pelos handlers de evento do elemento de áudio, que agora fica
   *  montado o tempo todo: `error` de src vazio enquanto ocioso não pode
   *  restaurar volume. */
  const locucaoSrcRef       = useRef<string | null>(null);

  const revogarBlob = useCallback((urlRemota: string) => {
    const blob = blobsRef.current.get(urlRemota);
    blobsRef.current.delete(urlRemota);
    // toBlobUrl devolve a própria URL remota quando o cache não está
    // disponível (TV Box com sandbox restrita) — nada a revogar nesse caso.
    if (blob && blob !== urlRemota) URL.revokeObjectURL(blob);
  }, []);

  /** Baixa as narrações para blob local assim que a config chega, para o
   *  disparo não depender de rede. Arquivos de 3-5s, poucos por empresa. */
  const prepararBlobs = useCallback(async (clipes: IdentidadeSonoraClipe[]) => {
    const urls = clipes.map(c => c.url);

    for (const urlRemota of [...blobsRef.current.keys()]) {
      if (!urls.includes(urlRemota)) revogarBlob(urlRemota);
    }

    await mediaCache.preCache(urls);
    for (const url of urls) {
      if (blobsRef.current.has(url)) continue;
      blobsRef.current.set(url, await mediaCache.toBlobUrl(url));
    }
  }, [revogarBlob]);

  // Carrega config + clipes ativos no mount e recarrega a cada poucos minutos —
  // a TV roda 24/7, então mudanças no admin (liga/desliga, intervalo, clipes)
  // precisam chegar sem depender de reload manual.
  useEffect(() => {
    if (!telaId) return;
    let cancelado = false;
    // Identidade estável (useRef(new Map())): copiado aqui só para o cleanup
    // não ler `blobsRef.current` depois do unmount.
    const blobs = blobsRef.current;

    const carregar = () => {
      identidadeSonoraPlayerRepository.buscarParaTela(telaId)
        .then(({ config, clipes }) => {
          if (cancelado) return;
          ativoRef.current = config?.ativo ?? false;
          intervaloRef.current = config?.intervalo_faixas ?? 0;
          clipesRef.current = clipes;
          void prepararBlobs(clipes).catch(() => { /* fallback: URL remota no disparo */ });
        }).catch(() => {
          if (!cancelado) ativoRef.current = false;
        });
    };

    carregar();
    const interval = setInterval(carregar, RECARGA_CONFIG_MS);

    return () => {
      cancelado = true;
      clearInterval(interval);
      for (const urlRemota of [...blobs.keys()]) revogarBlob(urlRemota);
    };
  }, [telaId, prepararBlobs, revogarBlob]);

  const limparSafetyTimeout = useCallback(() => {
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
  }, []);

  const restaurarVolume = useCallback(() => {
    locucaoAtivaRef.current = false;
    limparSafetyTimeout();
    // Pausa antes de limpar o src: o elemento fica montado o tempo todo, e
    // largar um áudio tocando enquanto o src é removido gera erro de mídia.
    const audio = locucaoAudioRef.current;
    if (audio && !audio.paused) audio.pause();
    // Volume ATUAL do Player, não o capturado no disparo: se o garçom mudou o
    // volume durante a locução, voltar ao antigo divergia do Controle Remoto.
    ytSlideRef.current?.setVolume(playerVolumeRef.current);
    locucaoSrcRef.current = null;
    setLocucaoSrc(null);
  }, [ytSlideRef, playerVolumeRef, limparSafetyTimeout, locucaoAtivaRef]);

  const handleLocucaoEnded = useCallback(() => {
    restaurarVolume();
  }, [restaurarVolume]);

  const handleLocucaoError = useCallback(() => {
    // Com o elemento de áudio montado permanentemente, remover o src dispara
    // um `error` de mídia vazia. Ignora: não há locução em curso para restaurar.
    if (locucaoSrcRef.current === null) return;
    restaurarVolume();
  }, [restaurarVolume]);

  // Dispara a reprodução explicitamente (nunca via `autoPlay`) e agenda o
  // timeout de segurança — cobre tanto rejeição de play() (autoplay
  // bloqueado) quanto stream travado sem nunca emitir `error`.
  useEffect(() => {
    if (!locucaoSrc) return;
    const audio = locucaoAudioRef.current;
    if (!audio) return;

    // Só rebobina quando já há mídia carregada. Fazer `currentTime = 0` antes
    // dos metadados chegarem força um seek no decoder ainda vazio e o
    // Chromium descarta os primeiros frames — era o corte literal do início.
    if (audio.readyState >= 1 /* HAVE_METADATA */ && audio.currentTime > 0) {
      audio.currentTime = 0;
    }
    // Nivelamento da narração (ganho medido no upload) por cima do volume do
    // Controle Remoto. Teto em 1: correção para cima não pode estourar.
    audio.volume = Math.min(1, Math.max(0,
      (volumePreLocucaoRef.current / 100) * ganhoAtualRef.current));
    audio.play().catch(() => handleLocucaoError());
    safetyTimeoutRef.current = setTimeout(() => restaurarVolume(), SAFETY_TIMEOUT_MS);

    return () => limparSafetyTimeout();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- locucaoTick força o replay mesmo com a mesma URL após interrupção.
  }, [locucaoSrc, locucaoTick]);

  // Troca de faixa: se uma locução ainda estava tocando, interrompe e restaura
  // antes de avaliar o gatilho da nova faixa — nunca abaixa por cima de si mesma.
  useEffect(() => {
    if (videoIdAtual === videoIdAnteriorRef.current) return;
    videoIdAnteriorRef.current = videoIdAtual;

    if (locucaoSrc !== null) restaurarVolume();

    if (!habilitado || !videoIdAtual || !ativoRef.current) return;

    contadorFaixasRef.current += 1;

    if (!deveDispararLocucao(contadorFaixasRef.current, intervaloRef.current)) return;
    if (playerMutedRef.current) return;

    const clipe = selecionarClipeLocucao(clipesRef.current);
    if (!clipe) return;

    const volumeAtual = playerVolumeRef.current;
    volumePreLocucaoRef.current = volumeAtual;
    ganhoAtualRef.current = clipe.ganho ?? GANHO_PADRAO;
    locucaoAtivaRef.current = true;
    ytSlideRef.current?.setVolume(Math.round(volumeAtual * DUCK_RATIO));
    // Blob local quando já baixado; URL remota é o fallback (primeiro ciclo
    // antes do pré-carregamento terminar, ou plataforma sem Cache Storage).
    const src = blobsRef.current.get(clipe.url) ?? clipe.url;
    locucaoSrcRef.current = src;
    setLocucaoSrc(src);
    setLocucaoTick(t => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- habilitado só reavalia no gatilho da troca de faixa, não sozinho.
  }, [videoIdAtual]);

  return { locucaoAudioRef, locucaoSrc, handleLocucaoEnded, handleLocucaoError };
}
